import { Role, RoleAssignment } from '@adornis/abac/policies/SimpleRolePolicy.js';
import { Int } from '@adornis/baseql/baseqlTypes.js';
import type { EntityData } from '@adornis/baseql/entities/types.js';
import { registerQuery, registerSubscription } from '@adornis/baseql/metadata/register.js';
import { context } from '@adornis/baseql/server/context.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { subscribePaginated } from '@adornis/baseql/server/resolver/mongo.js';
import { watchQuery } from '@adornis/baseql/server/watchQuery.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { AdornisUser } from '@adornis/users/db/a-user.js';
import { defer, EMPTY, from, switchMap } from 'rxjs';
import { assertCanReadPlatformUser, canReadPlatformUser } from './assert-can-read-platform-user.js';
import { gatePlatformUserManageSubscription } from './nexus-permission-gate.js';

export const subscribePlatformRoleAssignmentFor = registerSubscription({
  operationName: 'subscribePlatformRoleAssignmentFor',
  type: () => RoleAssignment,
  params: [{ name: 'userId', type: () => String }],
  resolve: (targetUserId: string) => (_gqlFields: BaseQLSelectionSet<RoleAssignment>) =>
    defer(() => from(canReadPlatformUser(targetUserId))).pipe(
      switchMap(allowed => {
        if (!allowed) return EMPTY;
        return watchQuery(RoleAssignment._collectionName, { userId: targetUserId }).pipe(
          switchMap(async (): Promise<RoleAssignment | null> => {
            const col = await getRawCollection<EntityData<RoleAssignment>>(RoleAssignment._collectionName);
            const doc = await col.findOne({ userId: targetUserId });
            return doc ? new RoleAssignment(doc as EntityData<RoleAssignment>) : null;
          }),
        );
      }),
    ),
});

export const subscribePlatformUsersPaginated = registerSubscription({
  operationName: 'subscribePlatformUsersPaginated',
  type: () => [AdornisUser],
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'filter', type: () => AdornisFilter },
  ],
  resolve:
    (skip: number, limit: number, filter: AdornisFilter) => (gqlFields: BaseQLSelectionSet<AdornisUser>) =>
      gatePlatformUserManageSubscription(() => {
        const mongoFilter = filter.getMongoFilter();
        const options = filter.getMongoOptions();
        const classFilter =
          Object.keys(mongoFilter).length > 0
            ? { $and: [{ _class: AdornisUser._class }, mongoFilter] }
            : { _class: AdornisUser._class };
        return subscribePaginated(AdornisUser, skip, limit, classFilter, options);
      }),
});

export const getPlatformUserById = registerQuery({
  operationName: 'getPlatformUserById',
  type: () => AdornisUser,
  params: [{ name: 'userId', type: () => String }],
  resolve:
    (targetUserId: string) => async (gqlFields: BaseQLSelectionSet<AdornisUser>): Promise<AdornisUser | null> => {
      await assertCanReadPlatformUser(targetUserId);
      const col = await getRawCollection<EntityData<AdornisUser>>(AdornisUser._collectionName);
      const doc = await col.findOne({ _id: targetUserId });
      return doc ? new AdornisUser(doc as EntityData<AdornisUser>) : null;
    },
});

/** Roles for the signed-in user only (e.g. Nexus topbar). */
export const subscribeCurrentUserRolesForDisplay = registerSubscription({
  operationName: 'subscribeCurrentUserRolesForDisplay',
  type: () => [Role],
  params: [],
  resolve: () => (_gqlFields: BaseQLSelectionSet<Role>) => {
    const signedInUserId = (context as { userID?: string }).userID;
    if (!signedInUserId) return EMPTY;

    return watchQuery(RoleAssignment._collectionName, { userId: signedInUserId }).pipe(
      switchMap(async (): Promise<Role[]> => {
        const assignmentCol = await getRawCollection<RoleAssignment>(RoleAssignment._collectionName);
        const assignment = await assignmentCol.findOne({ userId: signedInUserId });
        if (!assignment?.roleIds?.length) return [];
        const roleCol = await getRawCollection<Role>(Role._collectionName);
        const docs = await roleCol.find({ _id: { $in: assignment.roleIds } }).toArray();
        return docs.map(doc => new Role(doc as EntityData<Role>));
      }),
    );
  },
});
